'use client';

import { useState } from 'react';
import { deleteInvoice } from '@/actions';
import { AlertDialogComponent } from '@/components/alert-dialog';
import { LoadingButton } from '@/components/loading-button';
import { useSubmitStatus } from '@/hooks/useSubmitStatus';
import { useTranslations } from '@/hooks/useTranslations';
import { Table } from '@tanstack/react-table';
import { Trash2 } from 'lucide-react';

interface BulkInvoiceActionsProps<TData> {
  table: Table<TData>;
}

export function BulkInvoiceActions<TData>({
  table,
}: BulkInvoiceActionsProps<TData>) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { status: deleteStatus, wrap: wrapDelete } = useSubmitStatus();
  const t = useTranslations('InvoiceTable');
  const common = useTranslations('Common');

  const selectedRows = table.getSelectedRowModel().rows;
  const ids = selectedRows
    .map((row) => {
      const original = row.original as { id?: string };
      return original.id;
    })
    .filter((id): id is string => typeof id === 'string');

  const handleBulkDelete = () => {
    wrapDelete(async () => {
      for (const id of ids) {
        const result = await deleteInvoice(id);
        if (result?.error) {
          throw new Error(result.error);
        }
      }
    }).then(() => {
      selectedRows.forEach((row) => row.toggleSelected(false));
      setTimeout(() => setDeleteOpen(false), 700);
    });
  };

  return (
    <>
      <button
        className="text-red-600 hover:underline font-medium flex items-center gap-1"
        onClick={() => setDeleteOpen(true)}
      >
        <Trash2 className="h-4 w-4" />
        {t('deleteSelected')}
      </button>

      <AlertDialogComponent
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t('deleteSelectedInvoices')}
        description={t('deleteSelectedInvoicesDesc').replace(
          '{count}',
          String(ids.length)
        )}
        cancelLabel={common('cancel')}
        deleteLabel={
          <LoadingButton
            isFull={false}
            type="button"
            status={deleteStatus}
            idleText={common('delete')}
            loadingText={common('deleting')}
            successText={common('deleted')}
            errorText={common('failed')}
            onClick={handleBulkDelete}
          />
        }
      />
    </>
  );
}
